/**
 * A project's money (design 3.10, step M4): its stage, funding, markup, funder and customer; its
 * estimates and their lines; the invoices, credit notes and payments recorded against it; and
 * budget against actual. Shown to those who may view the project's costs; changed by those who may
 * manage them. Every write is dry-run first and can be undone from the audit log.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  Container,
  CostCategory,
  CostRecord,
  CostSummary,
  Estimate,
  ProjectContact,
  ProjectFinance,
  RateCard,
  type CostRecordKind,
  type EstimateLine,
  type LineRepeat,
  type ProjectStage,
} from "@workcore/schemas";
import { ApiError, api, downloadFile, get, uploadFile } from "../../api";
import { Paperclip } from "lucide-react";
import { Breadcrumbs, EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, toast } from "../../ui";
import { confirmAction } from "../../confirm";
import { InlineText } from "../editor/shared";
import { fmt, toAmount, toPence, useCurrency } from "../../money";

const key = (slug: string, id: number, what: string) => ["ws", slug, "container", id, what];
const MOVES: Record<ProjectStage, ProjectStage[]> = { proposed: ["live", "declined"], declined: ["proposed"], live: ["closed"], closed: ["live"] };
const MOVE_LABEL: Record<ProjectStage, string> = { live: "Go live", declined: "Decline", proposed: "Propose again", closed: "Close" };
const KIND_LABEL: Record<CostRecordKind, string> = { debit: "Invoice", credit: "Credit note", income: "Payment received" };
const today = () => new Date().toISOString().slice(0, 10);
/** What the API will store (design 3.11); it checks the bytes itself, this only steers the file picker. */
const FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.md,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp";

interface WriteReq {
  method: string;
  path: string;
  body?: unknown;
}

export function ProjectMoneyPage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const q = useQueryClient();
  const cur = useCurrency(slug);
  const container = useQuery({ queryKey: key(slug, id, "detail"), queryFn: async () => Container.parse(await get(`/v1/ws/${slug}/containers/${id}`)) });
  const finance = useQuery({ queryKey: key(slug, id, "finance"), queryFn: async () => ProjectFinance.parse(await get(`/v1/ws/${slug}/containers/${id}/finance`)), retry: false });
  const write = useMutation({
    mutationFn: async (w: WriteReq) => {
      await api(w.method, w.path, { ...(w.body !== undefined ? { body: w.body } : {}), dryRun: true });
      return api(w.method, w.path, w.body !== undefined ? { body: w.body } : {});
    },
    onSuccess: () => toast("Saved"),
    onSettled: () => q.invalidateQueries({ queryKey: ["ws", slug, "container", id] }),
  });
  const c = container.data;
  if (!c) return <ErrorBox error={container.error} />;
  const header = (
    <PageHeader
      back={<Breadcrumbs items={[{ label: "Projects", to: `/w/${slug}/projects` }, { label: c.name }]} />}
      title={`${c.name}: money`}
      description={c.isTemplate ? "A template's funding, markup and draft estimate come to each project made from it." : "What the project should cost, what it has cost and, if chargeable, what it earns."}
    />
  );
  if (finance.error instanceof ApiError && finance.error.status === 403) {
    return (
      <>
        {header}
        <EmptyState title="You cannot see this project's money" body="Money is shown to the project's admins and to anyone granted 'view costs' on it (Access, on the project)." />
      </>
    );
  }
  const f = finance.data;
  if (!f) return <>{header}<ErrorBox error={finance.error} /></>;
  const can = f.canManage;
  return (
    <>
      {header}
      <ErrorBox error={write.error} />
      <Settings slug={slug} c={c} f={f} can={can} write={write.mutate} />
      {!c.isTemplate && <Summary slug={slug} id={id} cur={cur} />}
      <Estimates slug={slug} c={c} f={f} can={can} cur={cur} write={write.mutate} />
      {!c.isTemplate && <Records slug={slug} id={id} can={can} cur={cur} write={write.mutate} />}
    </>
  );
}

function useProjectContacts(slug: string, id: number) {
  return useQuery({ queryKey: key(slug, id, "contacts"), queryFn: async () => z.array(ProjectContact).parse(await get(`/v1/ws/${slug}/containers/${id}/contacts`)) });
}
function useCategories(slug: string) {
  return useQuery({ queryKey: ["ws", slug, "cost-categories"], queryFn: async () => z.array(CostCategory).parse(await get(`/v1/ws/${slug}/cost-categories`)) });
}

// ---------- stage, funding, parties ----------

function Settings({ slug, c, f, can, write }: { slug: string; c: Container; f: ProjectFinance; can: boolean; write: (w: WriteReq) => void }) {
  const contacts = useProjectContacts(slug, c.id).data ?? [];
  const orgs = [...new Map(contacts.filter((l) => l.contact.kind === "organisation").map((l) => [l.contactId, l.contact])).values()];
  const path = `/v1/ws/${slug}/containers/${c.id}/finance`;
  const move = async (to: ProjectStage) => {
    if (to === "declined" || to === "closed") {
      const ok = await confirmAction({ title: `${MOVE_LABEL[to]} '${c.name}'?`, body: to === "declined" ? "The bid did not go ahead. You can propose it again later." : "The project is finished. It can be reopened.", action: MOVE_LABEL[to], danger: false, check: () => api("POST", `/v1/ws/${slug}/containers/${c.id}/stage`, { body: { stage: to }, dryRun: true }) });
      if (!ok) return;
    }
    write({ method: "POST", path: `/v1/ws/${slug}/containers/${c.id}/stage`, body: { stage: to } });
  };
  const party = (label: string, field: "funderContactId" | "customerContactId") => (
    <Field label={label} help={orgs.length === 0 ? "Add organisations to the project's contacts first." : undefined}>
      <select aria-label={label} disabled={!can} value={f[field] ?? ""} onChange={(e) => write({ method: "PATCH", path, body: { [field]: e.target.value ? Number(e.target.value) : null } })}>
        <option value="">none</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </Field>
  );
  return (
    <Panel title="Funding">
      {!c.isTemplate && (
        <div className="inline wrap toolbar">
          <span>Stage <StatusBadge status={f.stage} /></span>
          <span className="spacer" />
          {can && MOVES[f.stage].map((to) => <button key={to} className={to === "live" ? "" : "secondary"} onClick={() => void move(to)}>{MOVE_LABEL[to]}</button>)}
        </div>
      )}
      <fieldset className="choices horizontal" aria-label="Funding" disabled={!can}>
        <label className="choice">
          <input type="radio" name="funding" checked={f.funding === "chargeable"} onChange={() => write({ method: "PATCH", path, body: { funding: "chargeable" } })} />
          <span><strong>Chargeable</strong><span className="muted small">We charge a customer or funder: price is cost plus a markup.</span></span>
        </label>
        <label className="choice">
          <input type="radio" name="funding" checked={f.funding === "funded"} onChange={() => write({ method: "PATCH", path, body: { funding: "funded" } })} />
          <span><strong>Funded</strong><span className="muted small">An investment by a department or group: the estimate is the budget.</span></span>
        </label>
      </fieldset>
      <div className="inline wrap toolbar">
        {f.funding === "chargeable" && (
          <Field label="Markup on cost">
            <span>
              <InlineText label="markup percent" disabled={!can} value={f.markupPercent === null ? "" : String(f.markupPercent)} onSave={(v) => write({ method: "PATCH", path, body: { markupPercent: v.trim() === "" ? null : Number(v) } })} /> %
            </span>
          </Field>
        )}
        {party("Funder", "funderContactId")}
        {party("Customer", "customerContactId")}
      </div>
    </Panel>
  );
}

// ---------- budget against actual ----------

function Summary({ slug, id, cur }: { slug: string; id: number; cur: string }) {
  const s = useQuery({ queryKey: key(slug, id, "costs"), queryFn: async () => CostSummary.parse(await get(`/v1/ws/${slug}/containers/${id}/costs`)) });
  const d = s.data;
  if (!d) return <ErrorBox error={s.error} />;
  return (
    <Panel title="Budget against actual">
      {!d.estimate && <p className="muted">No accepted estimate yet, so there is no budget. Actual spend is still counted.</p>}
      {d.byCategory.length > 0 && (
        <table className="grid money">
          <thead><tr><th>Category</th><th>Budget</th><th>Actual</th><th>Left</th><th>Over</th></tr></thead>
          <tbody>
            {d.byCategory.map((b) => (
              <tr key={b.categoryId}>
                <td>{b.label}</td><td>{fmt(b.budgetPence, cur)}</td><td>{fmt(b.actualPence, cur)}</td><td>{fmt(b.remainingPence, cur)}</td>
                <td className={b.overPence ? "over" : "muted"}>{b.overPence ? fmt(b.overPence, cur) : "—"}</td>
              </tr>
            ))}
            <tr className="total"><td>Total</td><td>{fmt(d.budgetPence, cur)}</td><td>{fmt(d.actualPence, cur)}</td><td>{fmt(d.remainingPence, cur)}</td><td /></tr>
          </tbody>
        </table>
      )}
      <dl className="figures">
        {d.estimate && <><dt>Budget from</dt><dd>estimate v{d.estimate.number}</dd></>}
        <dt>VAT on costs</dt><dd>{fmt(d.costVatPence, cur)}</dd>
        {d.funding === "chargeable" && (
          <>
            <dt>Price</dt><dd>{fmt(d.pricePence, cur)}</dd>
            <dt>Received</dt><dd>{fmt(d.incomePence, cur)}</dd>
            <dt>Expected profit</dt><dd className={(d.expectedProfitPence ?? 0) < 0 ? "over" : ""}>{fmt(d.expectedProfitPence, cur)}</dd>
            <dt>Profit to date</dt><dd className={(d.profitToDatePence ?? 0) < 0 ? "over" : ""}>{fmt(d.profitToDatePence, cur)}</dd>
          </>
        )}
      </dl>
    </Panel>
  );
}

// ---------- estimates ----------

function Estimates({ slug, c, f, can, cur, write }: { slug: string; c: Container; f: ProjectFinance; can: boolean; cur: string; write: (w: WriteReq) => void }) {
  const list = useQuery({ queryKey: key(slug, c.id, "estimates"), queryFn: async () => z.array(Estimate).parse(await get(`/v1/ws/${slug}/containers/${c.id}/estimates`)) });
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const rows = list.data ?? [];
  const shown = open ?? rows[0]?.id ?? null;
  return (
    <Panel title="Estimates" actions={can ? <button onClick={() => setAdding(true)}>New estimate</button> : undefined}>
      <ErrorBox error={list.error} />
      {list.data && rows.length === 0 && <EmptyState title="No estimates yet" body={c.isTemplate ? "A draft estimate here is copied to every project made from this template, dated from its start." : "Start one, add its lines, then submit and accept it. The accepted estimate is the budget."} />}
      {rows.length > 0 && (
        <table className="grid money">
          <thead><tr><th>Version</th><th>Status</th><th>Period</th><th>Cost</th>{f.funding === "chargeable" && <th>Price</th>}<th /></tr></thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className={e.id === shown ? "selected" : ""}>
                <td><button className="link" onClick={() => setOpen(e.id)}>v{e.number}</button></td>
                <td><StatusBadge status={e.status} /></td>
                <td>{e.periodStart ? `${e.periodStart} to ${e.periodEnd}` : e.periodLengthDays !== null ? `${e.periodLengthDays} days from day ${e.periodOffsetDays}` : <span className="muted">—</span>}</td>
                <td>{fmt(e.totals.costPence, cur)}</td>
                {f.funding === "chargeable" && <td>{fmt(e.totals.pricePence, cur)}</td>}
                <td className="row-actions" />
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {shown !== null && <EstimateEditor key={shown} slug={slug} c={c} id={shown} can={can} cur={cur} write={write} />}
      <NewEstimate slug={slug} c={c} open={adding} onDone={(newId) => (setAdding(false), newId && setOpen(newId))} existing={rows} />
    </Panel>
  );
}

function NewEstimate({ slug, c, open, onDone, existing }: { slug: string; c: Container; open: boolean; onDone: (id?: number) => void; existing: Estimate[] }) {
  const q = useQueryClient();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [copy, setCopy] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...(copy ? { copyFrom: Number(copy) } : {}),
        ...(c.isTemplate ? (from && to ? { periodOffsetDays: Number(from), periodLengthDays: Number(to) } : {}) : from && to ? { periodStart: from, periodEnd: to } : {}),
      };
      const path = `/v1/ws/${slug}/containers/${c.id}/estimates`;
      await api("POST", path, { body, dryRun: true });
      return Estimate.parse(await api("POST", path, { body }));
    },
    onSuccess: (e) => (toast(`Started v${e.number}`), void q.invalidateQueries({ queryKey: ["ws", slug, "container", c.id] }), onDone(e.id)),
  });
  return (
    <SidePanel title="New estimate" open={open} onClose={() => onDone()} onSubmit={() => save.mutate()} submitLabel="Start estimate" busy={save.isPending} error={save.error}>
      {existing.length > 0 && (
        <Field label="Start from" help="Copies the period and every line, so a new version starts where the last one ended.">
          <select aria-label="Copy from" value={copy} onChange={(e) => setCopy(e.target.value)}>
            <option value="">nothing (empty)</option>
            {existing.map((e) => <option key={e.id} value={e.id}>v{e.number} ({e.status})</option>)}
          </select>
        </Field>
      )}
      {c.isTemplate ? (
        <>
          <Field label="Starts this many days after the project's start"><input aria-label="Period offset" type="number" min={0} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Length in days"><input aria-label="Period length" type="number" min={1} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </>
      ) : (
        <>
          <Field label="Period from" help="Needed for lines that repeat each week, month or year."><input aria-label="Period from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Period to"><input aria-label="Period to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </>
      )}
    </SidePanel>
  );
}

const REPEAT_LABEL: Record<LineRepeat, string> = { once: "once", week: "each week", month: "each month", year: "each year" };

function EstimateEditor({ slug, c, id, can, cur, write }: { slug: string; c: Container; id: number; can: boolean; cur: string; write: (w: WriteReq) => void }) {
  const e = useQuery({ queryKey: key(slug, c.id, `estimate-${id}`), queryFn: async () => Estimate.parse(await get(`/v1/ws/${slug}/estimates/${id}`)) });
  const cats = useCategories(slug).data ?? [];
  const d = e.data;
  if (!d) return <ErrorBox error={e.error} />;
  const draft = d.status === "draft";
  const edit = can && draft;
  const catName = (cid: number) => cats.find((x) => x.id === cid)?.label ?? `#${cid}`;
  const act = async (verb: "submit" | "accept" | "reject" | "delete") => {
    const words = {
      submit: { title: `Submit v${d.number}?`, body: "It is locked from now on: its lines, funding and markup no longer change. Make a new version to change it.", action: "Submit" },
      accept: { title: `Accept v${d.number}?`, body: "It becomes the project's budget. An estimate accepted earlier is superseded.", action: "Accept" },
      reject: { title: `Reject v${d.number}?`, body: "It is closed. You can start a new version from it.", action: "Reject" },
      delete: { title: `Delete draft v${d.number}?`, body: "Its number is not used again. It can be brought back from the audit log.", action: "Delete" },
    }[verb];
    const path = verb === "delete" ? `/v1/ws/${slug}/estimates/${id}` : `/v1/ws/${slug}/estimates/${id}/${verb}`;
    const method = verb === "delete" ? "DELETE" : "POST";
    if (await confirmAction({ ...words, danger: verb === "delete" || verb === "reject", check: () => api(method, path, { dryRun: true }) })) write({ method, path });
  };
  const lines = d.lines ?? [];
  return (
    <div className="estimate">
      <div className="inline wrap toolbar">
        <h3>Estimate v{d.number}</h3>
        <StatusBadge status={d.status} />
        <span className="muted small">{d.funding}{d.markupPercent !== null ? `, markup ${d.markupPercent}%` : ""}{draft && d.funding === "chargeable" ? " (from the project until submitted)" : ""}</span>
        <span className="spacer" />
        {can && draft && !c.isTemplate && <button onClick={() => void act("submit")}>Submit</button>}
        {can && d.status === "submitted" && <><button onClick={() => void act("accept")}>Accept</button><button className="secondary" onClick={() => void act("reject")}>Reject</button></>}
        {can && draft && <button className="secondary" onClick={() => void act("delete")}>Delete draft</button>}
      </div>
      {lines.length === 0 ? (
        <p className="muted">No lines yet.</p>
      ) : (
        <table className="grid money">
          <thead><tr><th>Category</th><th>Description</th><th>Quantity</th><th>Each</th><th>Repeats</th><th>Cost</th>{d.funding === "chargeable" && <th>Price</th>}<th /></tr></thead>
          <tbody>
            {lines.map((l) => <LineRow key={l.id} slug={slug} l={l} cat={catName(l.categoryId)} edit={edit} cur={cur} chargeable={d.funding === "chargeable"} write={write} />)}
            <tr className="total">
              <td colSpan={5}>Total</td><td>{fmt(d.totals.costPence, cur)}</td>{d.funding === "chargeable" && <td>{fmt(d.totals.pricePence, cur)}</td>}<td />
            </tr>
          </tbody>
        </table>
      )}
      {d.funding === "chargeable" && d.totals.profitPence !== null && <p className="muted small">Profit at this price: {fmt(d.totals.profitPence, cur)}</p>}
      {edit && <AddLine slug={slug} c={c} est={d} cats={cats} write={write} />}
    </div>
  );
}

function LineRow({ slug, l, cat, edit, cur, chargeable, write }: { slug: string; l: EstimateLine; cat: string; edit: boolean; cur: string; chargeable: boolean; write: (w: WriteReq) => void }) {
  const path = `/v1/ws/${slug}/estimate-lines/${l.id}`;
  return (
    <tr>
      <td>{cat}</td>
      <td>
        <InlineText label={`line ${l.id} description`} disabled={!edit} value={l.description} onSave={(v) => write({ method: "PATCH", path, body: { description: v } })} />
        {l.object && <div className="muted small">{l.object.visible ? `#${l.object.id} ${l.object.name}` : `#${l.object.id}`}</div>}
      </td>
      <td>
        <InlineText label={`line ${l.id} quantity`} disabled={!edit} value={String(l.quantity)} onSave={(v) => write({ method: "PATCH", path, body: { quantity: Number(v) } })} /> {l.unit}
      </td>
      <td>{l.rateCardId !== null || !edit ? fmt(l.unitCostPence, cur) : <InlineText label={`line ${l.id} unit cost`} disabled={!edit} value={toAmount(l.unitCostPence)} onSave={(v) => { const p = toPence(v); if (p === null) toast("Not an amount"); else write({ method: "PATCH", path, body: { unitCostPence: p } }); }} />}</td>
      <td>{l.repeat === "once" ? "once" : `${REPEAT_LABEL[l.repeat]} (×${l.repeats})`}</td>
      <td>{fmt(l.costPence, cur)}</td>
      {chargeable && <td>{fmt(l.pricePence, cur)}{l.markupPercent !== null && <span className="muted small"> at {l.markupPercent}%</span>}</td>}
      <td className="row-actions">{edit && <RowMenu label={`Actions for line ${l.id}`} items={[{ label: "Remove", danger: true, onSelect: () => write({ method: "DELETE", path }) }]} />}</td>
    </tr>
  );
}

function AddLine({ slug, c, est, cats, write }: { slug: string; c: Container; est: Estimate; cats: CostCategory[]; write: (w: WriteReq) => void }) {
  const rates = useQuery({ queryKey: ["ws", slug, "rate-cards"], queryFn: async () => z.array(RateCard).parse(await get(`/v1/ws/${slug}/rate-cards`)), retry: false }).data ?? [];
  const contacts = useProjectContacts(slug, c.id).data ?? [];
  const suppliers = [...new Map(contacts.map((l) => [l.contactId, l.contact])).values()];
  const [category, setCategory] = useState("");
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");
  const [cost, setCost] = useState("");
  const [rate, setRate] = useState("");
  const [repeat, setRepeat] = useState<LineRepeat>("once");
  const [supplier, setSupplier] = useState("");
  const cat = cats.find((x) => x.id === Number(category));
  const labour = cat?.kind === "internal_labour";
  const hasPeriod = !!est.periodStart || est.periodLengthDays !== null;
  const pence = toPence(cost);
  const ready = !!cat && Number(qty) >= 0 && qty !== "" && (labour ? !!rate : pence !== null);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    write({
      method: "POST",
      path: `/v1/ws/${slug}/estimates/${est.id}/lines`,
      body: {
        categoryId: cat!.id,
        description: desc,
        quantity: Number(qty),
        repeat,
        ...(labour ? { rateCardId: Number(rate) } : { unitCostPence: pence, unit }),
        ...(supplier ? { contactId: Number(supplier) } : {}),
      },
    });
    setDesc("");
    setQty("1");
    setCost("");
  };
  return (
    <form autoComplete="off" className="inline wrap add-line" onSubmit={submit}>
      <label className="inline-field"><span>Category</span><select aria-label="Line category" required value={category} onChange={(e) => (setCategory(e.target.value), setRate(""))}>
        <option value="">choose…</option>
        {cats.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
      </select></label>
      <label className="inline-field"><span>Description</span><input aria-label="Line description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Felt, 10 m rolls" /></label>
      <label className="inline-field"><span>Quantity</span><input aria-label="Line quantity" inputMode="decimal" required value={qty} onChange={(e) => setQty(e.target.value)} /></label>
      {labour ? (
        <label className="inline-field"><span>Rate</span><select aria-label="Line rate card" required value={rate} onChange={(e) => setRate(e.target.value)}>
          <option value="">{rates.length ? "choose…" : "no rate cards"}</option>
          {rates.map((r) => <option key={r.id} value={r.id}>{r.label}, {fmt(r.costPence)}/{r.unit}</option>)}
        </select></label>
      ) : (
        <>
          <label className="inline-field"><span>Unit</span><input aria-label="Line unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="each" /></label>
          <label className="inline-field"><span>Each</span><input aria-label="Line unit cost" inputMode="decimal" required value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" /></label>
        </>
      )}
      <label className="inline-field"><span>Repeats</span><select aria-label="Line repeats" value={repeat} onChange={(e) => setRepeat(e.target.value as LineRepeat)}>
        {(["once", "week", "month", "year"] as const).map((r) => <option key={r} value={r} disabled={r !== "once" && !hasPeriod}>{REPEAT_LABEL[r]}</option>)}
      </select></label>
      {suppliers.length > 0 && (
        <label className="inline-field"><span>Supplier</span><select aria-label="Line supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)}>
          <option value="">none</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></label>
      )}
      <button type="submit" disabled={!ready}>Add line</button>
      {!hasPeriod && <p className="muted small">Give the estimate a period to add lines that repeat each week, month or year.</p>}
    </form>
  );
}

// ---------- cost records ----------

function Records({ slug, id, can, cur, write }: { slug: string; id: number; can: boolean; cur: string; write: (w: WriteReq) => void }) {
  const [deleted, setDeleted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [attachTo, setAttachTo] = useState<CostRecord | null>(null);
  const list = useQuery({ queryKey: key(slug, id, `records-${deleted}`), queryFn: async () => z.array(CostRecord).parse(await get(`/v1/ws/${slug}/containers/${id}/cost-records`, deleted ? { include_deleted: "true" } : {})) });
  const cats = useCategories(slug).data ?? [];
  const contacts = useProjectContacts(slug, id).data ?? [];
  const who = (cid: number) => contacts.find((l) => l.contactId === cid)?.contact.name ?? `#${cid}`;
  const rows = list.data ?? [];
  return (
    <Panel title="Invoices and payments" actions={can ? <button onClick={() => setAdding(true)}>Record…</button> : undefined}>
      <ErrorBox error={list.error} />
      <div className="inline wrap toolbar"><label className="check"><input type="checkbox" checked={deleted} onChange={(e) => setDeleted(e.target.checked)} /> show deleted</label></div>
      {list.data && rows.length === 0 && <EmptyState title="Nothing recorded yet" body="Record invoices and credit notes as they arrive, and payments as they come in. Each counts against the budget at once." />}
      {rows.length > 0 && (
        <table className="grid money">
          <thead><tr><th>Date</th><th>What</th><th>From</th><th>Category</th><th>Net</th><th>VAT</th><th>Reference</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={r.deleted ? "inactive" : ""}>
                <td>{r.date}</td>
                <td><span className={`kind kind-${r.kind}`}>{KIND_LABEL[r.kind]}</span> {r.title}{r.deleted && <span className="muted small"> deleted</span>}</td>
                <td>{who(r.contactId)}</td>
                <td>{r.categoryId === null ? <span className="muted">—</span> : (cats.find((x) => x.id === r.categoryId)?.label ?? `#${r.categoryId}`)}</td>
                <td>{r.kind === "credit" ? `−${fmt(r.netPence, cur)}` : fmt(r.netPence, cur)}</td>
                <td>{fmt(r.vatPence, cur)}</td>
                <td>
                  {r.invoiceUrl ? <a href={r.invoiceUrl} target="_blank" rel="noreferrer noopener">{r.reference || "invoice"}</a> : r.reference}
                  {r.attachments.map((a) => (
                    <span key={a.id} className="file-chip">
                      <button type="button" className="link" title={`Download ${a.name}`} onClick={() => void downloadFile(`/v1/ws/${slug}/attachments/${a.id}/content`, a.name).catch((e: Error) => toast(e.message))}>
                        <Paperclip size={12} strokeWidth={1.5} aria-hidden="true" /> {a.name}
                      </button>
                      {can && !r.deleted && (
                        <button type="button" className="link chip-x" aria-label={`Remove ${a.name}`} onClick={() => void confirmAction({ title: `Remove '${a.name}'?`, body: "It is taken off the record and kept; it can be brought back from the audit log.", action: "Remove", check: () => api("DELETE", `/v1/ws/${slug}/attachments/${a.id}`, { dryRun: true }) }).then((ok) => ok && write({ method: "DELETE", path: `/v1/ws/${slug}/attachments/${a.id}` }))}>×</button>
                      )}
                    </span>
                  ))}
                </td>
                <td className="row-actions">
                  {can && (
                    <RowMenu
                      label={`Actions for ${r.title}`}
                      items={[!r.deleted && { label: "Attach a file…", onSelect: () => setAttachTo(r) }, r.deleted ? { label: "Restore", onSelect: () => write({ method: "POST", path: `/v1/ws/${slug}/cost-records/${r.id}/restore` }) } : { label: "Delete…", danger: true, onSelect: () => void confirmAction({ title: `Delete '${r.title}'?`, body: "It stops counting against the budget. It can be restored.", action: "Delete", check: () => api("DELETE", `/v1/ws/${slug}/cost-records/${r.id}`, { dryRun: true }) }).then((ok) => ok && write({ method: "DELETE", path: `/v1/ws/${slug}/cost-records/${r.id}` })) }]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <NewRecord slug={slug} id={id} open={adding} onDone={() => setAdding(false)} cats={cats} contacts={contacts} />
      <AttachFile slug={slug} id={id} record={attachTo} onDone={() => setAttachTo(null)} />
    </Panel>
  );
}

function AttachFile({ slug, id, record, onDone }: { slug: string; id: number; record: CostRecord | null; onDone: () => void }) {
  const q = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const save = useMutation({
    mutationFn: () => uploadFile(`/v1/ws/${slug}/cost-records/${record!.id}/attachments/upload`, file!),
    onSuccess: () => (toast("Attached"), setFile(null), void q.invalidateQueries({ queryKey: ["ws", slug, "container", id] }), onDone()),
  });
  return (
    <SidePanel title={`Attach a file to '${record?.title ?? ""}'`} open={record !== null} onClose={() => (setFile(null), onDone())} onSubmit={() => save.mutate()} submitLabel="Attach" busy={save.isPending || !file} error={save.error}>
      <Field label="File" help="The invoice, credit note or remittance: PDF, Word, Excel, an image or text, up to 25 MB.">
        <input aria-label="File to attach" type="file" accept={FILE_TYPES} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
    </SidePanel>
  );
}

function NewRecord({ slug, id, open, onDone, cats, contacts }: { slug: string; id: number; open: boolean; onDone: () => void; cats: CostCategory[]; contacts: ProjectContact[] }) {
  const q = useQueryClient();
  const people = [...new Map(contacts.map((l) => [l.contactId, l.contact])).values()];
  const [kind, setKind] = useState<CostRecordKind>("debit");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState("");
  const [category, setCategory] = useState("");
  const [net, setNet] = useState("");
  const [vat, setVat] = useState("");
  const [reference, setReference] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const save = useMutation({
    mutationFn: async () => {
      const netP = toPence(net);
      const vatP = vat.trim() === "" ? 0 : toPence(vat);
      if (netP === null || vatP === null) throw new Error("Amounts must be numbers like 1200 or 1200.50");
      const body = {
        kind, title, date, contactId: Number(from), netPence: netP, vatPence: vatP, reference,
        ...(kind !== "income" ? { categoryId: Number(category) } : {}),
        ...(link.trim() ? { invoiceUrl: link.trim() } : {}),
      };
      const path = `/v1/ws/${slug}/containers/${id}/cost-records`;
      await api("POST", path, { body, dryRun: true });
      const rec = CostRecord.parse(await api("POST", path, { body }));
      if (file) {
        try {
          await uploadFile(`/v1/ws/${slug}/cost-records/${rec.id}/attachments/upload`, file);
        } catch (e) {
          void q.invalidateQueries({ queryKey: ["ws", slug, "container", id] });
          throw new Error(`Recorded, but the file was not stored: ${(e as Error).message}. Attach it from the record's ⋯ menu.`);
        }
      }
      return rec;
    },
    onSuccess: () => {
      toast("Recorded");
      setTitle("");
      setNet("");
      setVat("");
      setReference("");
      setLink("");
      setFile(null);
      setFileKey((k) => k + 1);
      void q.invalidateQueries({ queryKey: ["ws", slug, "container", id] });
      onDone();
    },
  });
  return (
    <SidePanel title="Record money" open={open} onClose={onDone} onSubmit={() => save.mutate()} submitLabel="Record" busy={save.isPending || !from || !title || (kind !== "income" && !category)} error={save.error}>
      <Field label="This is">
        <select aria-label="Record kind" value={kind} onChange={(e) => setKind(e.target.value as CostRecordKind)}>
          <option value="debit">an invoice (adds cost)</option>
          <option value="credit">a credit note (reduces cost)</option>
          <option value="income">a payment received</option>
        </select>
      </Field>
      <Field label="Title"><input aria-label="Record title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Timber, first delivery" /></Field>
      <Field label="Date"><input aria-label="Record date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field label={kind === "income" ? "Paid by" : "From"} help={people.length === 0 ? "Add them to the project's contacts first." : undefined}>
        <select aria-label="Record from" required value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">choose…</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      {kind !== "income" && (
        <Field label="Category">
          <select aria-label="Record category" required value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">choose…</option>
            {cats.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </Field>
      )}
      <Field label="Net amount"><input aria-label="Record net" required inputMode="decimal" value={net} onChange={(e) => setNet(e.target.value)} placeholder="0.00" /></Field>
      <Field label="VAT"><input aria-label="Record VAT" inputMode="decimal" value={vat} onChange={(e) => setVat(e.target.value)} placeholder="0.00" /></Field>
      <Field label="Reference" help="The invoice or remittance number."><input aria-label="Record reference" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      <Field label="The invoice itself" help="PDF, Word, Excel, an image or text, up to 25 MB. Stored with the record and shown only to those who see the project's money.">
        <input key={fileKey} aria-label="Record file" type="file" accept={FILE_TYPES} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
      <Field label="Or a link to it" help="If the document is kept elsewhere (SharePoint, Drive…)."><input aria-label="Record link" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" /></Field>
    </SidePanel>
  );
}
